import React, { memo, FC } from 'react';
import {
  StatusCompleteFilled,
  StatusAlertFilled,
  StatusWarningFilled,
  InfoCircleFilled,
} from '@wix/wix-ui-icons-common';

import Text from '../Text';
import Toast, { ToastProps } from '../Toast';
import { StatusToastProps, ToastStatus } from './StatusToast.types';
import { useToastTimeout } from './hooks/useToastTimeout';
import { st, classes } from './StatusToast.st.css';
import ToastAction from './components/ToastAction/ToastAction';
import ToastLink from './components/ToastLink/ToastLink';

const DEFAULT_STATUS: ToastStatus = 'info';
const SUCCESS_AUTO_DISMISS_DELAY = 7000;

const iconByStatus: Record<ToastStatus, React.ReactNode> = {
  success: <StatusCompleteFilled />,
  error: <StatusAlertFilled />,
  warning: <StatusWarningFilled />,
  info: <InfoCircleFilled />,
};

const roleByStatus: Record<ToastStatus, ToastProps['role']> = {
  success: 'status',
  error: 'alert',
  warning: 'alert',
  info: 'status',
};

const StatusToast: FC<StatusToastProps> & {
  Action: typeof ToastAction;
  Link: typeof ToastLink;
} = memo(
  ({
    status = DEFAULT_STATUS,
    action,
    children,
    className,
    duration,
    ...rest
  }: StatusToastProps) => {
    const autoDismissEnabled = duration !== null;
    const defaultAutoDismissDuration = ['info', 'success'].includes(status)
      ? SUCCESS_AUTO_DISMISS_DELAY
      : undefined;

    useToastTimeout(
      autoDismissEnabled ? (duration ?? defaultAutoDismissDuration) : undefined,
      rest.onDismiss,
    );

    return (
      <Toast
        role={roleByStatus[status]}
        className={st(classes.toast, className)}
        {...rest}
      >
        <div
          data-hook="status-toast-root"
          className={st(classes.root, {
            success: status === 'success',
            error: status === 'error',
            warning: status === 'warning',
          })}
          data-status={status}
        >
          <div className={classes.content}>
            <div className={classes.icon}>
              {iconByStatus[status] ?? iconByStatus[DEFAULT_STATUS]}
            </div>
            <Text
              light
              size="medium"
              dataHook="status-toast-content"
              className={classes.text}
            >
              {children}
            </Text>
          </div>
          {action && <div className={classes.action}>{action}</div>}
        </div>
      </Toast>
    );
  },
) as any;

StatusToast.displayName = 'StatusToast';
StatusToast.Action = ToastAction;
StatusToast.Link = ToastLink;

export default StatusToast;
